// Statistiche del negozio (vetrina avanzata): visite, provenienza, creazioni più viste,
// ordini e incassi dai clienti portati dall'artigiano. Dati da GET /api/shops/me/stats.
import React, { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  ArrowDownRight, ArrowUpRight, Eye, Info, MousePointerClick, PiggyBank, QrCode, RefreshCw,
  ShoppingBag, ShoppingCart, TrendingUp, Users,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { fetchMyShopStats } from '../../lib/shopsApi';

const TEAL = '#008080';
const PERIODS = [7, 30, 90];

const eur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: v >= 1000 ? 0 : 2 }).format(v || 0);
const num = (v) => new Intl.NumberFormat('it-IT').format(v || 0);
const dayLabel = (iso, days) => {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('it-IT', days > 7 ? { day: 'numeric', month: 'short' } : { weekday: 'short', day: 'numeric' });
};

function Delta({ value }) {
  if (value === null || value === undefined) return <span className="text-xs text-slate-400">nessun confronto</span>;
  if (value === 0) return <span className="text-xs text-slate-500">invariato</span>;
  const up = value >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${up ? 'text-emerald-700' : 'text-rose-700'}`}>
      <Icon className="w-3.5 h-3.5" aria-hidden />
      {up ? '+' : ''}{String(value).replace('.', ',')}%
    </span>
  );
}

function Kpi({ icon: Icon, label, value, delta, hint, testId }) {
  return (
    <div className="rounded-xl border bg-white p-4" data-testid={testId}>
      <p className="text-xs text-slate-500 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" aria-hidden />{label}</p>
      <p className="mt-1.5 text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
      <div className="mt-1 min-h-[1rem]">{delta !== undefined ? <Delta value={delta} /> : <span className="text-xs text-slate-400">{hint}</span>}</div>
    </div>
  );
}

function DailyTooltip({ active, payload, days }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-semibold text-slate-800 mb-1">{dayLabel(d.date, Math.min(days, 7))}</p>
      <p className="text-slate-600">{num(d.visits)} {d.visits === 1 ? 'visita' : 'visite'}</p>
      {d.shop_orders > 0 && <p className="text-slate-600">{d.shop_orders} {d.shop_orders === 1 ? 'ordine' : 'ordini'} · {eur(d.shop_revenue)}</p>}
    </div>
  );
}

function BarList({ rows, max, render, empty }) {
  if (!rows.length) return <p className="text-sm text-slate-400 py-3">{empty}</p>;
  return (
    <ul className="space-y-2.5">
      {rows.map((r) => (
        <li key={r.key}>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-slate-700 truncate">{r.label}</span>
            <span className="text-slate-900 font-medium tabular-nums shrink-0">{render(r)}</span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.max(3, (r.value / (max || 1)) * 100)}%`, background: TEAL }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function ShopStats({ isPublished, slug }) {
  const [days, setDays] = useState(30);
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchMyShopStats(days)
      .then((data) => active && setState({ loading: false, error: null, data }))
      .catch((e) => active && setState({ loading: false, error: e.message, data: null }));
    return () => { active = false; };
  }, [days, reloadKey]);

  const { loading, error, data } = state;
  const t = data?.totals;

  return (
    <Card className="p-5 md:p-6 mt-4" data-testid="shop-stats">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#008080]" /> Statistiche del negozio</h3>
          <p className="text-sm text-slate-500">Chi visita il tuo negozio, da dove arriva e quanto compra.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border bg-slate-50 p-0.5" role="tablist" aria-label="Periodo">
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                role="tab"
                aria-selected={days === p}
                onClick={() => setDays(p)}
                className={`px-3 h-8 rounded-md text-sm transition-colors ${days === p ? 'bg-white shadow-sm font-semibold text-slate-900' : 'text-slate-500 hover:text-slate-800'}`}
                data-testid={`shop-stats-period-${p}`}
              >
                {p} giorni
              </button>
            ))}
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={() => setReloadKey((k) => k + 1)} aria-label="Aggiorna" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {!isPublished && (
        <p className="mt-4 text-sm rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 flex gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0" /> Il negozio non è pubblicato: le visite si contano da quando i clienti possono vederlo.
        </p>
      )}
      {data && !data.tracking_ready && (
        <p className="mt-4 text-sm rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 flex gap-2" data-testid="shop-stats-not-ready">
          <Info className="w-4 h-4 mt-0.5 shrink-0" /> Il conteggio delle visite non è ancora attivo: per ora vedi solo ordini e incassi.
        </p>
      )}

      {error && (
        <div className="mt-6 text-center py-8">
          <p className="text-sm text-slate-600">Non riusciamo a caricare le statistiche ({error}).</p>
          <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => setReloadKey((k) => k + 1)}>Riprova</Button>
        </div>
      )}

      {!data && loading && (
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 animate-pulse">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 rounded-xl bg-slate-100" />)}
          <div className="col-span-2 lg:col-span-4 h-56 rounded-xl bg-slate-100" />
        </div>
      )}

      {data && !error && (
        <div className={`mt-5 space-y-6 transition-opacity ${loading ? 'opacity-60' : ''}`}>
          {/* Indicatori principali (variazioni rispetto al periodo precedente) */}
          <p className="-mb-3 text-xs text-slate-400">Variazioni rispetto ai {days} giorni precedenti</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={Users} label="Visite" value={num(t.visits)} delta={data.changes.visits} testId="shop-stats-visits" />
            <Kpi icon={ShoppingBag} label="Ordini dai tuoi clienti" value={num(t.shop_orders)} delta={data.changes.shop_orders} testId="shop-stats-orders" />
            <Kpi icon={TrendingUp} label="Incassi dai tuoi clienti" value={eur(t.shop_revenue)} delta={data.changes.shop_revenue} testId="shop-stats-revenue" />
            <Kpi
              icon={MousePointerClick}
              label="Conversione"
              value={t.conversion === null ? '—' : `${String(t.conversion).replace('.', ',')}%`}
              hint="ordini ogni 100 visite"
              testId="shop-stats-conversion"
            />
          </div>

          {/* Commissione risparmiata (Pro) o risparmiabile */}
          {data.fee.amount > 0 && (
            <div className={`rounded-xl px-4 py-3 flex items-start gap-3 ${data.fee.is_pro ? 'bg-emerald-50 border border-emerald-200' : 'bg-[#DAA520]/10 border border-[#DAA520]/30'}`} data-testid="shop-stats-fee">
              <PiggyBank className={`w-5 h-5 mt-0.5 shrink-0 ${data.fee.is_pro ? 'text-emerald-700' : 'text-[#B8860B]'}`} />
              <p className="text-sm text-slate-700">
                {data.fee.is_pro ? (
                  <><strong>{eur(data.fee.amount)} di commissioni risparmiate</strong> in questo periodo: sugli ordini dei clienti che porti tu la commissione Rarifex ({String(data.fee.pct).replace('.', ',')}%) non si paga.</>
                ) : (
                  <>Con il piano <strong>Pro</strong> sugli ordini dei clienti che porti tu la commissione Rarifex è 0%: in questo periodo avresti risparmiato <strong>{eur(data.fee.amount)}</strong>.</>
                )}
              </p>
            </div>
          )}

          {/* Andamento */}
          <div>
            <div className="flex items-baseline justify-between gap-3 mb-2">
              <p className="text-sm font-semibold text-slate-800">Visite giorno per giorno</p>
              <p className="text-xs text-slate-500">{num(t.page_views)} pagine viste · {num(t.product_views)} schede prodotto · {num(t.carts)} carrelli</p>
            </div>
            {t.visits === 0 ? (
              <div className="h-48 rounded-xl border border-dashed flex flex-col items-center justify-center text-center px-6" data-testid="shop-stats-empty">
                <QrCode className="w-8 h-8 text-slate-300" />
                <p className="mt-2 text-sm text-slate-600">Ancora nessuna visita in questo periodo.</p>
                <p className="text-xs text-slate-400 mt-1">Condividi il link o il QR del negozio: le visite compariranno qui.</p>
              </div>
            ) : (
              <div className="h-56" data-testid="shop-stats-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.daily} margin={{ top: 4, right: 4, left: -20, bottom: 0 }} barCategoryGap={days > 30 ? 1 : 2}>
                    <CartesianGrid vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="date" tickFormatter={(v) => dayLabel(v, days)} tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} minTickGap={16} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={44} />
                    <Tooltip cursor={{ fill: 'rgba(0,128,128,0.08)' }} content={<DailyTooltip days={days} />} />
                    <Bar dataKey="visits" fill={TEAL} radius={[4, 4, 0, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Provenienza */}
            <div data-testid="shop-stats-sources">
              <p className="text-sm font-semibold text-slate-800 mb-3">Da dove arrivano</p>
              <BarList
                rows={data.sources.map((s) => ({ key: s.source, label: s.label, value: s.visits, carts: s.carts }))}
                max={data.sources[0]?.visits}
                render={(r) => <>{num(r.value)}{r.carts > 0 && <span className="text-xs text-slate-400 font-normal ml-1.5">· {r.carts} al carrello</span>}</>}
                empty="Nessuna visita nel periodo."
              />
              <p className="text-xs text-slate-400 mt-3">
                Vuoi distinguere un canale? Aggiungi <code className="bg-slate-100 px-1 rounded">&amp;via=nome</code> al link del negozio
                {slug ? <> (es. <code className="bg-slate-100 px-1 rounded">…?ref={slug}&amp;via=fiera</code>)</> : null}.
              </p>
            </div>

            {/* Creazioni */}
            <div data-testid="shop-stats-products">
              <p className="text-sm font-semibold text-slate-800 mb-3">Creazioni più viste</p>
              {data.top_products.length ? (
                <ul className="divide-y">
                  {data.top_products.map((p) => (
                    <li key={p.product_id} className="py-2 flex items-center gap-3">
                      {p.image ? <img src={p.image} alt="" className="w-10 h-10 rounded-md object-cover bg-slate-100" /> : <div className="w-10 h-10 rounded-md bg-slate-100" />}
                      <span className="flex-1 min-w-0 text-sm text-slate-700 truncate">{p.title}</span>
                      <span className="text-xs text-slate-500 tabular-nums flex items-center gap-1 shrink-0"><Eye className="w-3.5 h-3.5" />{num(p.views)}</span>
                      {p.sold > 0 && <span className="text-xs text-emerald-700 tabular-nums flex items-center gap-1 shrink-0"><ShoppingCart className="w-3.5 h-3.5" />{num(p.sold)} vend.</span>}
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-slate-400 py-3">Nessuna scheda prodotto vista nel periodo.</p>}
            </div>
          </div>

          {data.pages.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-slate-800 mb-2">Pagine più visitate</p>
              <div className="flex flex-wrap gap-2">
                {data.pages.map((p) => (
                  <span key={p.page} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm text-slate-700">
                    {p.label} <span className="text-slate-400 tabular-nums">{num(p.views)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-lg bg-slate-50 px-4 py-3 text-xs text-slate-500 space-y-1">
            <p>
              <strong className="text-slate-700">Tutti gli ordini della bottega</strong> nel periodo: {num(t.all_orders)} ({eur(t.all_revenue)}), di cui dai tuoi clienti {num(t.shop_orders)}.
            </p>
            {t.shop_requests !== null && t.shop_requests !== undefined && (
              <p data-testid="shop-stats-requests">
                <strong className="text-slate-700">Richieste su misura dal negozio</strong>: {num(t.shop_requests)}. Restano tue anche se la proposta viene pagata dopo i 30 giorni.
              </p>
            )}
            <p>"Tuoi clienti" sono quelli arrivati dal link o dal QR della bottega e che comprano entro 30 giorni. Le visite sono contate in forma anonima, senza cookie; non contano le tue visite né le anteprime.</p>
          </div>
        </div>
      )}
    </Card>
  );
}
